import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import NextGeoDataApi from '@features/api/nextApiService/nextGeoDataApi';
import Button from '@components/Button/Button';
import styles from '@components/LangPopup/LangPopup.styles';
import Icon from '@components/icons/Icon';
import sanitizeHtml from '@utils/sanitizeHtml';
import { useRegionsStore } from '@features/footer/components/LanguageSelect/mobX/context/RegionStoreProvider';
import browserStorage from '@utils/browserStorage';
import { useOffer } from '@hooks/useOffer';
import useCountry from '@hooks/useCountry';
import { OFFER_TO_REGION_MAP } from '@config/index';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
let getGeoData = false;
export type PropsType = {
  active: boolean;
  setActive: (active: boolean) => void;
  promotionalMessagesHeight: number;
};
const LangPopup = ({
  active,
  setActive,
  promotionalMessagesHeight
}: PropsType) => {
  const [defaultLangCode, setDefaultLangCode] = useState<string | null>(null);
  const [langCode, setLangCode] = useState<string | null>(null);
  const [langUrl, setLangUrl] = useState<string | null>(null);
  const {
    t,
    i18n
  } = useTranslation('common');
  const regionsStore = useRegionsStore();
  const popupRef = useRef<HTMLDivElement>(null);
  const {
    codeToName
  } = useCountry();
  const [offer] = useOffer();
  const {
    langPopupHeight,
    setLangPopupHeight,
    barPositionStatic
  } = layoutUiState;
  const findLangCode = (regions, newLangCode: string): string | null => {
    let code = null;
    regions.forEach(region => {
      if (code || !region?.languages) return;
      region.languages.forEach(language => {
        if (code || !language?.code) return;
        if (language.code.replace('_', '-') === newLangCode.replace('_', '-')) {
          code = language.code.replace('_', '-');
        }
      });
    });
    if (code) return code;
    regions.forEach(region => {
      if (code || !region?.languages) return;
      region.languages.forEach(language => {
        if (code || !language?.code) return;
        const splitLangCode = language.code.split('_');
        if (splitLangCode[0] && splitLangCode[1] && (splitLangCode[0].toLowerCase() === newLangCode || splitLangCode[1].toLowerCase() === newLangCode)) {
          code = language.code.replace('_', '-');
        }
      });
    });
    return code;
  };
  const findLangUrl = (regions, newLangCode: string): string | null => {
    let url = null;
    regions.forEach(region => {
      if (url || !region?.languages) return;
      region.languages.forEach(language => {
        if (url || !language?.url || !language?.code) return;
        if (language.code.replace('_', '-') === newLangCode.replace('_', '-')) {
          url = language.url;
        }
      });
    });
    if (url) return url;
    regions.forEach(region => {
      if (url || !region?.languages) return;
      region.languages.forEach(language => {
        if (url || !language?.url || !language?.code) return;
        const splitLangCode = language.code.split('_');
        if (splitLangCode[0] && splitLangCode[1] && (splitLangCode[0].toLowerCase() === newLangCode || splitLangCode[1].toLowerCase() === newLangCode)) {
          url = language.url;
        }
      });
    });
    return url;
  };
  const onResize = () => {
    if (popupRef?.current?.clientHeight && active) {
      const newPopupHeight = popupRef.current?.clientHeight;
      if (+newPopupHeight !== (langPopupHeight ? +langPopupHeight : 0)) {
        setLangPopupHeight(+newPopupHeight);
      }
    }
  };
  const init = async () => {
    let newLangUrl: string | null = null;
    getGeoData = true;
    const geoData = await NextGeoDataApi.getGeoData();
    if (!geoData) return;
    const {
      userGeolocation,
      locale
    } = geoData;
    if (userGeolocation?.country) {
      let newLangCode = findLangCode(regionsStore.regions, userGeolocation.country.toLowerCase());
      if (!newLangCode || !i18n.exists(`langPopup.switchButtonText_${newLangCode}`) || !i18n.exists(`langPopup.selectButtonText_${newLangCode}`)) newLangCode = 'en-EN';
      const newDefaultLangCode = findLangCode(regionsStore.regions, locale);
      if (regionsStore?.regions) {
        newLangUrl = newLangCode ? findLangUrl(regionsStore.regions, newLangCode) : 'global';
        setLangUrl(newLangUrl);
        setLangCode(newLangCode);
      }
      if (!i18n.exists(`langPopup.country_${newLangCode}_${newDefaultLangCode}`)) return;
      setDefaultLangCode(newDefaultLangCode || null);
      if (newLangUrl && newDefaultLangCode && newDefaultLangCode !== newLangCode) {
        setTimeout(() => {
          setActive(true);
          onResize();
        }, 1);
      }
    }
  };
  const getMessage = () => {
    const countryCode = OFFER_TO_REGION_MAP[offer];
    const lang = langCode ? langCode.split('-')[0] : undefined;
    let countryName = codeToName(countryCode, lang);
    if (!countryName) {
      countryName = countryCode;
    }
    const message = langCode ? t(`langPopup.message_${langCode}`, {
      country: countryName.toUpperCase()
    }) : '';
    return sanitizeHtml(message || '');
  };
  const getSwitchButtonText = () => {
    const switchButtonText = langCode ? t(`langPopup.switchButtonText_${langCode}`) : '';
    return switchButtonText || '';
  };
  const getSelectButtonText = () => {
    const selectButtonText = langCode ? t(`langPopup.selectButtonText_${langCode}`) : '';
    return selectButtonText || '';
  };
  const setLangPopupClose = () => browserStorage ? browserStorage.setItem(`langPopupClose`, 'true') : null;
  const getLangPopupClose = () => browserStorage ? browserStorage.getItem(`langPopupClose`) : null;
  const closeLangPopup = () => {
    setLangPopupClose();
    setActive(false);
    setLangPopupHeight(0);
  };
  useEffect(() => {
    if (!getGeoData && !getLangPopupClose()) {
      (async () => {
        await regionsStore.initializeData();
        init();
      })();
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [active, langPopupHeight]);
  return <>
      {getGeoData && langCode && defaultLangCode && langUrl && <div sx={styles.container(active, promotionalMessagesHeight, barPositionStatic)}>
          <div sx={styles.popup(active)} ref={popupRef}>
            <span sx={styles.message} dangerouslySetInnerHTML={getMessage()} />
            <div sx={styles.buttons}>
              <Link href={langUrl}>
                <Button variant="outlined" variantColor="white" size="small">
                  {getSwitchButtonText()}
                </Button>
              </Link>
              <Button variant="link" variantColor="white" size="micro" onClick={regionsStore.openSidebar}>
                {getSelectButtonText()}
              </Button>
              <Button addSx={styles.buttons.close} variant="iconOnly" variantColor="white" onClick={closeLangPopup} cy="GoToGlobalCloseButton">
                <Icon icon="Close3" size="24px" />
              </Button>
            </div>
          </div>
        </div>}
    </>;
};
export default observer(LangPopup);